import { type SupabaseClient, createClient } from '@supabase/supabase-js';

/**
 * Config passed to {@link createTenantClient}.
 *
 * Kept intentionally minimal — URL + anon key. The JWT that ships with each
 * request (carrying the `active_tenant_id` claim) drives RLS on the tenant
 * Supabase project; the anon key only asserts "I am a public web client".
 */
export type TenantClientConfig = {
  url: string;
  anonKey: string;
};

// TODO(phase-2): replace `any` with a generated `Database` type from
// `supabase gen types typescript`. Once generated it should live in
// `@sazeo/schemas` (or similar) and be imported here as a type-only import.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Database = any;

/**
 * Type alias for the tenant-scoped Supabase client.
 *
 * Exposed so wrapper packages (`@sazeo/auth`, `@sazeo/storage`,
 * `@sazeo/realtime`) can type their internal handles without re-importing
 * `@supabase/supabase-js` directly.
 */
export type TenantSupabaseClient = SupabaseClient<Database>;

/**
 * Create a fresh tenant-scoped Supabase client.
 *
 * Prefer {@link getTenantClient} in application code — it reads env vars and
 * memoizes. This factory exists for tests, SSR contexts, and multi-tenant
 * tooling that needs to juggle multiple clients.
 */
export function createTenantClient(config: TenantClientConfig): TenantSupabaseClient {
  if (!config.url || !config.anonKey) {
    throw new Error('[@sazeo/supabase-tenant] createTenantClient requires { url, anonKey }.');
  }

  return createClient<Database>(config.url, config.anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
}
